import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { slugify } from "./slugify";

function publicClient() {
  return createClient<Database>(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
  );
}

export const getInteractionByPair = createServerFn({ method: "GET" })
  .inputValidator((i: unknown) => {
    const slug = (i as any)?.pair;
    if (typeof slug !== "string" || !slug.includes("__")) throw new Error("Ungueltiger Pair-Slug");
    const [slugA, slugB] = slug.split("__");
    if (!slugA || !slugB) throw new Error("Ungueltiger Pair-Slug");
    return { slugA, slugB };
  })
  .handler(async ({ data }) => {
    const sb = publicClient();
    const { data: subs, error: subsErr } = await sb
      .from("substances")
      .select("id, name");
    if (subsErr) throw new Error(subsErr.message);

    const a = (subs ?? []).find((s) => slugify(s.name) === data.slugA);
    const b = (subs ?? []).find((s) => slugify(s.name) === data.slugB);
    if (!a || !b) return null;

    const [low, high] = [a.id, b.id].sort();
    const { data: row, error } = await sb
      .from("interaction_details")
      .select("id, risk_level, markers, content_de, published")
      .eq("substance_low_id", low)
      .eq("substance_high_id", high)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row || !row.published) {
      return { a: a.name, b: b.name, interaction: null };
    }
    return {
      a: a.name,
      b: b.name,
      interaction: {
        id: row.id,
        risk_level: row.risk_level as any,
        markers: (row.markers ?? []) as string[],
        content_de: (row.content_de ?? null) as any,
      },
    };
});

/**
 * Listet alle veroeffentlichten Kombinationen der Stufen "unsafe" und
 * "dangerous". Grundlage fuer die Notfall-Kurzreferenz.
 */
export const listDangerousCombinations = createServerFn({ method: "GET" })
  .handler(async () => {
    const sb = publicClient();
    const [{ data: subs, error: subsErr }, { data: rows, error }] = await Promise.all([
      sb.from("substances").select("id, name"),
      sb
        .from("interaction_details")
        .select("substance_low_id, substance_high_id, risk_level, markers")
        .eq("published", true)
        .in("risk_level", ["unsafe", "dangerous"]),
    ]);
    if (subsErr) throw new Error(subsErr.message);
    if (error) throw new Error(error.message);

    const nameById = new Map((subs ?? []).map((s) => [s.id, s.name]));
    return (rows ?? [])
      .map((r) => ({
        a: nameById.get(r.substance_low_id),
        b: nameById.get(r.substance_high_id),
        risk_level: r.risk_level as "unsafe" | "dangerous",
        markers: (r.markers ?? []) as string[],
      }))
      .filter((r): r is { a: string; b: string; risk_level: "unsafe" | "dangerous"; markers: string[] } =>
        Boolean(r.a && r.b),
      )
      .sort((x, y) => {
        if (x.risk_level !== y.risk_level) return x.risk_level === "dangerous" ? -1 : 1;
        return `${x.a}${x.b}`.localeCompare(`${y.a}${y.b}`, "de");
      });
  });
